import React, { FC, useEffect, useState } from 'react'

import Button from 'components/Common/Button'
import Heading from 'components/Common/Heading'
import ModalBox from 'components/Common/ModalBox'
import CalendarSettingsIcon from 'components/Common/SvgIcons/CalendarSettingsIcon'
import { CalendarToolsContainer, CreateEventContainer } from 'pages/Owner/container'
import {
  ConnectButtonWrapper,
  CalendarListContainer,
  RequestMeeting,
  ManageCalendarContainer,
} from 'components/Calendar/style'
import { ClientDataItemCircle } from 'components/DataReports/ClientReportManagement/ClientData/ClientDataItemCircle'
import { ICalendarList } from 'components/Calendar/types'
import { USER_ROLES } from 'config'
import { WarningIcon } from 'components/Common/SvgIcons/WarningIcon'
import ConnectCalenderIcon from 'components/Common/SvgIcons/ConnectCalendar'
import ToolTip from 'components/Common/ToolTip'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import CreateMeetingPopup from 'components/Calendar/CreateMeetingPopup'

export interface ICalendarSettings {
  meetingCalendarDetails: ICalendarList[]
  hasExternalCalendarConnected: boolean
  isUpdateUserAvailabilityLoading: boolean
  user?: any
  advisorMeetingRequestButtonEnabled?: boolean
}

interface ICalendarListItem {
  color: string
  name: string
  hasConnectionError: boolean
}

const CalendarSettings: FC<ICalendarSettings> = ({
  meetingCalendarDetails,
  hasExternalCalendarConnected,
  isUpdateUserAvailabilityLoading,
  user,
  advisorMeetingRequestButtonEnabled,
}) => {
  const [showCalendarTools, setShowCalendarTools] = useState<boolean>(false)
  const [showModalBox, setShowModalBox] = useState<boolean>(false)
  const [showCreateMeetingPopup, setShowCreateMeetingPopup] = useState<boolean>(false)

  const roles = useSelector((state: RootState) => state.user.user.roles)
  const isBOUser = roles && roles.includes(USER_ROLES.BUSINESS_OWNER)

  const isUserBSOUser = (): boolean => {
    const userRoles = user?.roles ?? []
    return userRoles.includes('BSO_ADVISOR') || userRoles.includes('BSO_ADMIN')
  }

  const isUserBusinessOwner = (): boolean => {
    const roles = user?.roles ?? []
    return roles[0] === 'BUSINESS_OWNER'
  }

  const onCloseHandler = () => {
    setShowCalendarTools(false)
    setShowModalBox(false)
    setShowCreateMeetingPopup(false)
  }

  useEffect(() => {
    if (!isUpdateUserAvailabilityLoading) {
      onCloseHandler()
    }
  }, [isUpdateUserAvailabilityLoading])

  return (
    <>
      <ManageCalendarContainer>
        <ConnectButtonWrapper className="flex flex-col !items-start justify-start mt-8">
          {/* Meet with your client button for BSO users */}
          {isUserBSOUser() && advisorMeetingRequestButtonEnabled && (
            <Button
              testId="meet-with-client"
              type="button"
              variant="primary"
              label="Create Meeting"
              onClick={() => setShowCreateMeetingPopup(true)}
            />
          )}
          {isUserBusinessOwner() && (
            <RequestMeeting className="sm:py-0 sm:pr-2">
              <Button
                testId="submit"
                type="button"
                variant="primary"
                label="Create Meeting"
                onClick={() => setShowCreateMeetingPopup(true)}
              />
            </RequestMeeting>
          )}
        </ConnectButtonWrapper>

        <Heading variant="secondary" className="!text-2xl tracking-wide mt-8 text-primary-text">
          Calendars:
        </Heading>
        <CalendarListContainer>
          {meetingCalendarDetails.map(({ name, color, hasConnectionError }, key) => (
            <CalendarListItem
              key={key}
              color={color}
              name={name}
              hasConnectionError={hasConnectionError}
            />
          ))}
        </CalendarListContainer>
        {hasExternalCalendarConnected ? (
          <Button
            testId="submit"
            type="button"
            variant="text"
            label="Manage Calendars"
            onClick={() => setShowCalendarTools(true)}
          />
        ) : (
          <Button
            type="button"
            variant="text"
            testId="submit"
            label="Connect a Calendar"
            onClick={() => setShowCalendarTools(true)}
            className="connectMeetingButtons !border-none"
            leftContent={() => <ConnectCalenderIcon className={''} />}
          />
        )}

        {!isBOUser && (
          <Button
            className="mt-12"
            testId="submit"
            type="button"
            variant="text"
            label="Meeting Settings"
            onClick={() => setShowModalBox(true)}
            leftContent={() => <CalendarSettingsIcon className={''} />}
          />
        )}
      </ManageCalendarContainer>

      {showCalendarTools && (
        <div>
          <ModalBox
            title="Connect with Calendar"
            onClose={onCloseHandler}
            height="xs:h-screen sm:h-auto"
            position="desktopTop"
            bgColor="bg-black/50 backdrop-blur-sm"
          >
            <CalendarToolsContainer />
          </ModalBox>
        </div>
      )}

      {/* New Create Meeting Popup */}
      <CreateMeetingPopup
        isOpen={showCreateMeetingPopup}
        onClose={() => setShowCreateMeetingPopup(false)}
      />

      {/* Meeting Settings Modal */}
      {showModalBox && isUserBSOUser() && (
        <div>
          <ModalBox
            title="Meeting Settings"
            onClose={onCloseHandler}
            height="h-full"
            padding="xs:p-6 sm:p-7"
            margin="sm:py-4"
            bgColor="bg-black/50 backdrop-blur-sm"
          >
            <CreateEventContainer onClose={setShowModalBox} />
          </ModalBox>
        </div>
      )}
    </>
  )
}

export default CalendarSettings

const CalendarListItem: FC<ICalendarListItem> = ({ color, name, hasConnectionError }) => {
  return (
    <div className="flex flex-start my-3 gap-2 items-center">
      <ClientDataItemCircle color={color} />
      <div className=" text-xs lg:text-base">{name}</div>
      {hasConnectionError && (
        <ToolTip
          name={'calendarSyncError'}
          text={'This account is expired. To reconnect, go to manage calendars'}
          className="text-center"
          width="185px"
          toolTipPadding={'8px 20px'}
        >
          {' '}
          <WarningIcon />{' '}
        </ToolTip>
      )}
    </div>
  )
}
